import { HttpAgent } from '@icp-sdk/core/agent'
import { Abi, encodeFunctionData } from 'viem'
import { config } from '../appConfig'
import OneSecClient, {
  ONESEC_CK_UNRWAP_CANISTER,
  ONESEC_MINTER_CANISTER,
} from '../clients/OneSecClient'
import { AMOUNT_TOO_LOW, UNKNOWN_ERROR, UNSUPPORTED_TOKEN } from '../constants/swapErrors'
import {
  BOB_ARB_ID,
  BOB_BASE_ID,
  BOB_ETH_ID,
  BOB_ICP_ID,
  CBBTC_ARB_ID,
  CBBTC_BASE_ID,
  CBBTC_ETH_ID,
  CBBTC_ICP_ID,
  CKBTC_ARB_ID,
  CKBTC_BASE_ID,
  CKBTC_ETH_ID,
  CKBTC_ICP_ID,
  CKUSDC_ICP_ID,
  CKUSDT_ICP_ID,
  GLDT_ARB_ID,
  GLDT_BASE_ID,
  GLDT_ETH_ID,
  GLDT_ICP_ID,
  ICP_ARB_ID,
  ICP_BASE_ID,
  ICP_ETH_ID,
  ICP_ICP_ID,
  USDC_ARB_ID,
  USDC_BASE_ID,
  USDC_ETH_ID,
  USDC_ICP_ID,
  USDT_ETH_ID,
  USDT_ICP_ID,
} from '../constants/tokenIds'
import { approveToken, customApproveErc20, ethCallContract } from '../handlers/walletActions/wallet'
import { EvmToken, ICToken, PlugToken } from '../interfaces/common'
import {
  SwapProvider,
  SwapProviderQuote,
  SwapProviderQuoteFetchError,
} from '../interfaces/swapInterface'
import { Wallet } from '../providers/WalletProvider'
import { GasCalculator } from '../utils/GasCalculator'
import { addMultiplier } from '../utils/bigint'
import { asEvmToken, asICToken } from '../utils/castToken'
import { callContractFunction } from '../utils/eth'
import { getEthProvider } from '../utils/getEthProvider'
import principalToBytes32 from '../utils/principalToBytes32'

const DEFAULT_ONESEC_GAS_LIMIT = 65000n

const HELPER_CONTRACT_MAP: { [tokenId: string]: string } = {
  [USDC_ETH_ID]: '0xAe2351B15cFf68b5863c6690dCA58Dce383bf45A',
  [USDC_BASE_ID]: '0xAe2351B15cFf68b5863c6690dCA58Dce383bf45A',
  [USDC_ARB_ID]: '0xAe2351B15cFf68b5863c6690dCA58Dce383bf45A',
  [USDT_ETH_ID]: '0xc5AC945a0af0768929301A27D6f2a7770995fAeb',
  [CBBTC_ETH_ID]: '0x7744c6a83E4b43921f27d3c94a742bf9cd24c062',
  [CBBTC_BASE_ID]: '0x7744c6a83E4b43921f27d3c94a742bf9cd24c062',
  [CBBTC_ARB_ID]: '0x7744c6a83E4b43921f27d3c94a742bf9cd24c062',
}

const encodeETHData = (amount: bigint, principalId: string, functionName: string) => {
  const abi: Abi = [
    {
      type: 'function',
      name: functionName,
      inputs: [
        { name: 'amount', type: 'uint256' },
        { name: 'data1', type: 'bytes32' },
      ],
      outputs: [],
      stateMutability: 'nonpayable',
    },
  ]

  const data = '0x00' + principalToBytes32(principalId).slice(2, -2)

  return encodeFunctionData({
    abi,
    functionName,
    args: [amount, data],
  })
}

export class OneSecBridgeClient {
  identityAgent: HttpAgent

  static get supportedTokens() {
    return [
      [ICP_ICP_ID, ICP_ETH_ID, ICP_ARB_ID, ICP_BASE_ID],
      [BOB_ICP_ID, BOB_ETH_ID, BOB_ARB_ID, BOB_BASE_ID],
      [CKBTC_ICP_ID, CKBTC_ETH_ID, CKBTC_ARB_ID, CKBTC_BASE_ID],
      [CBBTC_ICP_ID, CBBTC_ETH_ID, CBBTC_ARB_ID, CBBTC_BASE_ID],
      [USDT_ICP_ID, USDT_ETH_ID],
      [USDC_ICP_ID, USDC_ETH_ID, USDC_BASE_ID, USDC_ARB_ID],
      [GLDT_ICP_ID, GLDT_ETH_ID, GLDT_ARB_ID, GLDT_BASE_ID],
      [USDC_ICP_ID, CKUSDC_ICP_ID],
      [USDT_ICP_ID, CKUSDT_ICP_ID],
    ]
  }

  static isSupportedPair(fromToken: PlugToken, toToken: PlugToken) {
    // OneSec supports only swapping FROM CK tokens
    if (toToken.id === CKUSDC_ICP_ID || toToken.id === CKUSDT_ICP_ID) {
      return false
    }

    return this.supportedTokens.some(
      (tokenIds) => tokenIds.includes(fromToken.id) && tokenIds.includes(toToken.id)
    )
  }

  constructor(identityAgent: HttpAgent) {
    this.identityAgent = identityAgent
  }

  async getQuote(
    amount: bigint,
    fromToken: PlugToken,
    toToken: PlugToken,
    wallet: Wallet
  ): Promise<SwapProviderQuote | SwapProviderQuoteFetchError> {
    if (!wallet.ethAddress) {
      return { error: UNSUPPORTED_TOKEN, provider: SwapProvider.ONESEC }
    }

    try {
      const oneSecClient = new OneSecClient(config.getAgent())

      const fees = await oneSecClient.fetchFees(fromToken, toToken)
      let oneSecFee = fees ? addMultiplier(amount, fees.protocol_fee_in_percent) : 0n

      if (fromToken.id === CKUSDC_ICP_ID || fromToken.id === CKUSDT_ICP_ID) {
        if (!fees) {
          oneSecFee = addMultiplier(amount, 0.002)
        }

        oneSecFee += 880000n
      }

      const price = amount - oneSecFee

      if (fees && price < fees.min_amount) {
        return { error: AMOUNT_TOO_LOW, provider: SwapProvider.ONESEC }
      }

      let gasFee
      if (fromToken.evmChain) {
        const gas = await this.getGas(amount, asEvmToken(fromToken), wallet)
        const gasUnitPrice = gas.maxFeePerGas + gas.maxPriorityFeePerGas

        // oneSec gasLimit estimate will fail without allowance set, so use constant for now
        gasFee = (DEFAULT_ONESEC_GAS_LIMIT + gas.erc20ApproveGas) * gasUnitPrice
      }

      return {
        price: Number(price),
        priceHuman: Number(price) * 10 ** -toToken.decimals,
        provider: SwapProvider.ONESEC,
        metadata: { gasFee },
      }
    } catch (e) {
      return { error: UNKNOWN_ERROR, provider: SwapProvider.ONESEC }
    }
  }

  async swap(amount: bigint, fromToken: PlugToken, toToken: PlugToken, wallet: Wallet) {
    if (!wallet.ethAddress) throw new Error('Wallet ETH address is absent')

    if (fromToken.id === CKUSDC_ICP_ID || fromToken.id === CKUSDT_ICP_ID) {
      await this.unwrapCK(amount, asICToken(fromToken), wallet)
    } else if (fromToken.standard === 'ERC20') {
      await this.bridgeERC20(amount, asEvmToken(fromToken), wallet)
    } else {
      await this.bridgeICP(amount, asICToken(fromToken), asEvmToken(toToken), wallet)
    }

    const quote = await this.getQuote(amount, fromToken, toToken, wallet)
    return (quote as SwapProviderQuote).price
  }

  private async unwrapCK(amount: bigint, fromToken: ICToken, wallet: Wallet) {
    await approveToken({
      spender: { address: ONESEC_CK_UNRWAP_CANISTER },
      tokenCanisterId: fromToken.address,
      standard: fromToken.standard,
      amount: (amount + fromToken.fee).toString(),
    })

    await new OneSecClient(this.identityAgent).unwrapCK(amount, wallet.principalId, fromToken)
  }

  private async bridgeERC20(amount: bigint, fromToken: EvmToken, wallet: Wallet) {
    const provider = getEthProvider(fromToken.evmChain.rpcEndpoint)
    let nonce = await provider.getTransactionCount({ address: wallet.ethAddress as `0x${string}` })

    const gas = await this.getGas(amount, fromToken, wallet)

    const helperContract = HELPER_CONTRACT_MAP[fromToken.id]
    if (helperContract) {
      await customApproveErc20({
        value: amount.toString(),
        gasLimit: gas.erc20ApproveGas.toString(),
        maxFeePerGas: gas.maxFeePerGas.toString(),
        maxPriorityFeePerGas: gas.maxPriorityFeePerGas.toString(),
        nonce: nonce.toString(),
        contractAddress: fromToken.address!,
        spender: helperContract,
        chain: fromToken.evmChain,
      })

      nonce += 1
    }

    await new Promise((resolve) => setTimeout(resolve, 2000)) // wait 2 seconds for viem to catch up

    const data = encodeETHData(amount, wallet.principalId, helperContract ? 'lock1' : 'burn1')
    const gasLimit = await this.getOneSecGasLimit(amount, fromToken, wallet)

    await ethCallContract({
      gasLimit: gasLimit.toString(),
      maxFeePerGas: gas.maxFeePerGas.toString(),
      maxPriorityFeePerGas: gas.maxPriorityFeePerGas.toString(),
      nonce: nonce.toString(),
      contractAddress: helperContract || fromToken.address!,
      value: '0',
      data,
      chain: fromToken.evmChain,
    })
  }

  private async bridgeICP(amount: bigint, fromToken: ICToken, toToken: EvmToken, wallet: Wallet) {
    await approveToken({
      spender: { address: ONESEC_MINTER_CANISTER },
      tokenCanisterId: fromToken.address,
      standard: fromToken.standard,
      amount: amount.toString(),
    })

    await new OneSecClient(this.identityAgent).bridgeFromICP(
      amount,
      wallet.ethAddress!,
      wallet.principalId,
      fromToken,
      toToken
    )
  }

  private async getOneSecGasLimit(amount: bigint, token: EvmToken, wallet: Wallet) {
    const helperContract = HELPER_CONTRACT_MAP[token.id]
    const data = encodeETHData(amount, wallet.principalId, helperContract ? 'lock1' : 'burn1')

    const result = await callContractFunction(
      helperContract || token.address!,
      data,
      '0',
      wallet.ethAddress,
      undefined,
      token.evmChain
    )

    if (result?.gasLimit) {
      return result.gasLimit
    } else {
      throw new Error('Failed to load OneSec gasLimit')
    }
  }

  private async getGas(amount: bigint, token: EvmToken, wallet: Wallet) {
    const gasCalculator = new GasCalculator(token.evmChain)

    const gas = await gasCalculator.getEthGas(wallet.ethAddress, amount)
    const erc20ApproveGas = await gasCalculator.estimateErc20ApproveGas(
      wallet.ethAddress!,
      token.address!,
      Number(amount)
    )

    if (
      !erc20ApproveGas ||
      !gas ||
      typeof gas.maxFeePerGas === 'undefined' ||
      typeof gas.maxPriorityFeePerGas === 'undefined'
    ) {
      throw new Error('Failed to fetch gas price ')
    } else {
      return { ...gas, erc20ApproveGas }
    }
  }
}
